import { Loading } from "@/components/common/Loading/Loading"
import { Button } from "@/components/ui/button"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import type { TranslationKeys } from "@/hooks/useTranslation/useTranslation"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { useEffect } from "react"
import {
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
  useOutletContext,
  useParams,
} from "react-router"
import { useProcessingStatus } from "../../../api/questionnaireProcessingApi"
import { HorizontalCard } from "../../shared/HorizontalCard"
import { SectionLabel } from "../../shared/SectionLabel"
import { CUSTOMER_REQUESTS_BASE, customerRequestProcessPath } from "../routes"
import {
  canonicalStepForStatus,
  failedMessageKey,
  isFailedStatus,
  isInFlightStatus,
  isStepReachable,
  PROCESSING_STEP_ORDER,
  type ProcessingStepKey,
} from "./processingSteps"

const RADIAL_GLOW = {
  background:
    "radial-gradient(ellipse at center, rgba(255,244,64,0.18) 0%, rgba(255,244,64,0.04) 40%, transparent 70%)",
}

const STEP_LABEL_KEYS: Record<ProcessingStepKey, TranslationKeys> = {
  mapping: "MC_QP_STEP_MAPPING_TITLE",
  reconcile: "MC_QP_STEP_RECONCILE_TITLE",
  gaps: "MC_QP_STEP_GAPS_TITLE",
  review: "MC_QP_STEP_REVIEW_TITLE",
}

const STEP_SUBTITLE_KEYS: Record<ProcessingStepKey, TranslationKeys> = {
  mapping: "MC_QP_STEP_MAPPING_SUBTITLE",
  reconcile: "MC_QP_STEP_RECONCILE_SUBTITLE",
  gaps: "MC_QP_STEP_GAPS_SUBTITLE",
  review: "MC_QP_STEP_REVIEW_SUBTITLE",
}

const IN_FLIGHT_LABEL_KEYS: Record<string, TranslationKeys> = {
  mapping: "MC_QP_LOADING_MAPPING",
  reconciling: "MC_QP_LOADING_RECONCILING",
  answering: "MC_QP_LOADING_ANSWERING",
  rendering: "MC_QP_LOADING_RENDERING",
}

const STEP_PATH: Record<ProcessingStepKey, string> = {
  mapping: "mapping",
  reconcile: "reconcile",
  gaps: "gaps",
  review: "review",
}

export interface ProcessingOutletContext {
  questionnaireId: string
}

export function useProcessingOutletContext() {
  return useOutletContext<ProcessingOutletContext>()
}

export function QuestionnaireProcessingLayout() {
  const { translate } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { id } = useParams<{ id: string }>()
  const currentPathStep = location.pathname.split("/").pop()

  const { data: statusData, isLoading } = useProcessingStatus(id ?? null)
  const processingStatus = statusData?.processingStatus ?? null
  const canonicalStep = canonicalStepForStatus(processingStatus)

  useEffect(() => {
    if (!id || isLoading) return
    const canonicalPath = STEP_PATH[canonicalStep]

    if (isInFlightStatus(processingStatus)) {
      if (currentPathStep !== canonicalPath) {
        navigate(customerRequestProcessPath(id, canonicalPath), {
          replace: true,
        })
      }
      return
    }

    const requestedStep = PROCESSING_STEP_ORDER.find(
      (key) => STEP_PATH[key] === currentPathStep,
    )
    if (requestedStep && isStepReachable(processingStatus, requestedStep))
      return
    navigate(customerRequestProcessPath(id, canonicalPath), {
      replace: true,
    })
  }, [
    id,
    currentPathStep,
    processingStatus,
    canonicalStep,
    isLoading,
    navigate,
  ])

  if (!id) return <Navigate to={CUSTOMER_REQUESTS_BASE} replace />

  if (isLoading) {
    return (
      <div className="flex h-full flex-col items-center justify-center">
        <Loading />
      </div>
    )
  }

  const onCanonicalStepRoute = currentPathStep === STEP_PATH[canonicalStep]
  // "rendering" is excluded — ReviewStep triggers it itself right after the
  // user lands on review_ready (rather than the user clicking a "confirm and
  // advance" action on a previous step), and it already shows its own
  // scoped loading state in the excel-preview panel. Letting this full-page
  // loader take over too would unmount the already-visible review screen for
  // a moment and then remount it once rendering finishes — a jarring flicker
  // rather than a clean load.
  const showLoader =
    isInFlightStatus(processingStatus) &&
    onCanonicalStepRoute &&
    processingStatus !== "rendering"
  const failed = isFailedStatus(processingStatus)

  // "gaps" and "review" can both be the current route at the same
  // processing_status — highlight whichever step the URL is actually on
  // (falling back to the canonical step, e.g. while the loader is showing).
  const routeStep = PROCESSING_STEP_ORDER.find(
    (key) => STEP_PATH[key] === currentPathStep,
  )
  const activeStep =
    routeStep && isStepReachable(processingStatus, routeStep)
      ? routeStep
      : canonicalStep

  return (
    <div className="flex h-full min-h-0 w-full flex-1 flex-col overflow-hidden bg-card">
      <div className="mt-4 flex items-start justify-between px-6 pb-2">
        <div className="flex items-center">
          {PROCESSING_STEP_ORDER.map((key, index) => {
            const reachable = isStepReachable(processingStatus, key)
            const isActive = key === activeStep
            const isComplete = PROCESSING_STEP_ORDER.indexOf(activeStep) > index
            return (
              <div key={key} className="flex items-center">
                {index > 0 && <div className="h-px w-7 shrink-0 bg-grey4" />}
                <HorizontalCard
                  icon={index + 1}
                  title={translate(STEP_LABEL_KEYS[key])}
                  subtitle={translate(STEP_SUBTITLE_KEYS[key])}
                  size="compact"
                  isActive={isActive}
                  isComplete={isComplete}
                  disabled={!reachable}
                  onClick={() =>
                    reachable &&
                    navigate(customerRequestProcessPath(id, STEP_PATH[key]))
                  }
                />
              </div>
            )
          })}
        </div>
        <TooltipProvider delayDuration={300}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(CUSTOMER_REQUESTS_BASE)}
                aria-label={translate("MC_QP_EXIT_LABEL")}
              >
                ×
              </Button>
            </TooltipTrigger>
            <TooltipContent side="left" className="max-w-[220px]">
              {translate("MC_QP_EXIT_TOOLTIP")}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        {failed ? (
          <div className="flex h-full flex-col items-center justify-center gap-3">
            <p className="max-w-[520px] text-center text-[13px] text-red1">
              {translate(failedMessageKey(statusData?.failureCode))}
            </p>
          </div>
        ) : showLoader ? (
          <div className="relative flex h-full flex-col items-center justify-center">
            <div
              className="pointer-events-none absolute inset-0"
              style={RADIAL_GLOW}
            />
            <div className="relative flex flex-col items-center gap-4">
              <Loading />
              <SectionLabel>
                {translate(
                  IN_FLIGHT_LABEL_KEYS[processingStatus as string] ??
                    "MC_QP_LOADING_MAPPING",
                )}
              </SectionLabel>
            </div>
          </div>
        ) : (
          <Outlet
            context={{ questionnaireId: id } satisfies ProcessingOutletContext}
          />
        )}
      </div>
    </div>
  )
}
